import React, { useState } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
} from './table-management/designTokens';

interface BulkProvisionResult {
  totalProcessed: number;
  newlyProvisioned: number;
  alreadyLinked: number;
  errors: { guestRecordId: string; reason: string }[];
}

interface BulkMatchResult {
  totalScanned: number;
  autoLinked: number;
  suggestionsCreated: number;
  ignored: number;
}

interface ProvisionResponse {
  provisioning: BulkProvisionResult;
  matching: BulkMatchResult;
}

interface GuestAccountsPanelProps {
  eventId: string;
  onProvisioned?: () => void;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 0.75rem 0',
  },
  description: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    marginBottom: '1rem',
  },
  provisionBtn: {
    padding: '0.6rem 1.2rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  provisionBtnDisabled: {
    padding: '0.6rem 1.2rem',
    backgroundColor: '#ccc',
    color: '#666',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
  resultSection: {
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: BG_SURFACE,
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
  },
  resultTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: TEXT_DARK,
    marginBottom: '0.5rem',
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.3rem 0',
    fontSize: '0.85rem',
    color: TEXT_DARK,
  },
  statLabel: {
    color: TEXT_MUTED,
  },
  statValue: {
    fontWeight: 600,
  },
  errorSection: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#fdecea',
    borderRadius: '4px',
    border: '1px solid #f5c6cb',
  },
  errorTitle: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#d32f2f',
    marginBottom: '0.25rem',
  },
  errorItem: {
    fontSize: '0.8rem',
    color: '#d32f2f',
    padding: '0.15rem 0',
  },
  fetchError: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
  },
  loadingText: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    marginTop: '0.5rem',
  },
  divider: {
    height: '1px',
    backgroundColor: BORDER_LIGHT,
    margin: '0.75rem 0',
  },
};

export function GuestAccountsPanel({ eventId, onProvisioned }: GuestAccountsPanelProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ProvisionResponse | null>(null);

  async function handleProvision() {
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await apiClient<ProvisionResponse>(
        `/guest-accounts/provision/${eventId}`,
        { method: 'POST' },
      );
      setResult(response);
      onProvisioned?.();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to provision guest accounts.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Guest Account Provisioning</h3>
      <p style={styles.description}>
        Creates login accounts for all unlinked guests and runs smart member matching to detect existing members.
      </p>

      <button
        style={loading ? styles.provisionBtnDisabled : styles.provisionBtn}
        onClick={handleProvision}
        disabled={loading}
        aria-label="Provision guest accounts"
      >
        {loading ? 'Provisioning...' : 'Provision Guest Accounts'}
      </button>

      {loading && <p style={styles.loadingText}>Processing guest records...</p>}

      {error && <div style={styles.fetchError}>{error}</div>}

      {result && (
        <div style={styles.resultSection}>
          <div style={styles.resultTitle}>Provisioning Results</div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Newly Provisioned</span>
            <span style={styles.statValue}>{result.provisioning.newlyProvisioned}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Already Linked</span>
            <span style={styles.statValue}>{result.provisioning.alreadyLinked}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Total Processed</span>
            <span style={styles.statValue}>{result.provisioning.totalProcessed}</span>
          </div>

          <div style={styles.divider} />

          <div style={styles.resultTitle}>Matching Results</div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Auto-Linked (exact match)</span>
            <span style={styles.statValue}>{result.matching.autoLinked}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Suggestions Created</span>
            <span style={styles.statValue}>{result.matching.suggestionsCreated}</span>
          </div>
          <div style={styles.statRow}>
            <span style={styles.statLabel}>Total Scanned</span>
            <span style={styles.statValue}>{result.matching.totalScanned}</span>
          </div>

          {result.provisioning.errors.length > 0 && (
            <div style={styles.errorSection}>
              <div style={styles.errorTitle}>
                {result.provisioning.errors.length} error(s) during provisioning
              </div>
              {result.provisioning.errors.slice(0, 5).map((e, i) => (
                <div key={i} style={styles.errorItem}>
                  {e.reason}
                </div>
              ))}
              {result.provisioning.errors.length > 5 && (
                <div style={styles.errorItem}>
                  ...and {result.provisioning.errors.length - 5} more
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
